import { Box } from "@mui/material";
import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { SITE_MEDIA } from "src/domains/shared/constants/styles/site";
import { isTitlePhraseLanguage, splitTitlePhrases } from "./titlePhrases";

/** 문구 안 줄바꿈 태그(SiteText 와 같은 이름) */
const LINE_BREAK_TAG = /(<br\/>|<brd\/>|<brm\/>)/;

const BREAK_STYLES = {
  "<brd/>": { [SITE_MEDIA.phone]: { display: "none" } },
  "<brm/>": { display: "none", [SITE_MEDIA.phone]: { display: "inline" } },
} as const;

/**
 * 제목을 단어 단위로 아래에서 밀어 올리며 보이는 등장(시안 `.rv-words`). 서버 렌더에서 이미 단어로 나눠
 * 그리므로 스크립트가 DOM 을 고치지 않는다. 숨김·등장 스타일은 사이트 전역 규칙(`[data-reveal="words"]`)이 맡고,
 * 이 컴포넌트는 단어 상자(`.w`)와 순서(`--i`)만 만든다. 띄어 쓰지 않는 일본어·크메르어는 구가 단어 상자다.
 */
export function RevealWords({ text }: { text: string }) {
  const { i18n } = useTranslation();
  const language = i18n.language?.split("-")[0];
  const phrased = isTitlePhraseLanguage(language);
  const tokensOf = (chunk: string) =>
    phrased ? splitTitlePhrases(chunk, language) : chunk.split(/(\s+)/);
  let order = 0;
  const parts: ReactNode[] = [];
  text.split(LINE_BREAK_TAG).forEach((chunk, chunkIndex) => {
    if (chunk === "<br/>") {
      parts.push(<br key={`br-${chunkIndex}`} />);
      return;
    }
    if (chunk === "<brd/>" || chunk === "<brm/>") {
      parts.push(
        <Box component="br" key={`br-${chunkIndex}`} sx={BREAK_STYLES[chunk]} />
      );
      return;
    }
    tokensOf(chunk).forEach((token, tokenIndex) => {
      if (!token) return;
      const key = `${chunkIndex}-${tokenIndex}`;
      if (/^[\s\u200B]+$/u.test(token)) {
        parts.push(token);
        return;
      }
      parts.push(
        // 등장 효과를 끈 화면에서도 구 안에서 줄을 바꾸지 않게
        <span className="w" data-phrase={phrased ? "" : undefined} key={key}>
          <span style={{ "--i": order++ } as CSSProperties}>{token}</span>
        </span>
      );
    });
  });
  return <>{parts}</>;
}
